// src/pages/booking/BookingModal.jsx

import { useEffect, useState } from "react";
import moment from "moment";
import { X, Timer, CreditCard, Calendar } from "lucide-react";
import "./booking-modal.scss";

export default function BookingModal({ event, tier, onClose, onConfirm }) {
  const maxQty =
    tier.quota > 0 ? Math.max(tier.quota - (tier.sold || 0), 1) : 10;

  const [quantity, setQuantity] = useState(1);
  const [timeLeft, setTimeLeft] = useState(300);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const total = tier.price * quantity;
  const minutes = Math.floor(timeLeft / 60);
  const seconds = (timeLeft % 60).toString().padStart(2, "0");
  const isUrgent = timeLeft <= 60;

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(interval);
          onClose();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [onClose]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit() {
    if (!form.name || !form.email || !form.phone) return;
    if (submitting) return;
    setSubmitting(true);
    await onConfirm({
      name: form.name,
      email: form.email,
      phone: form.phone,
      quantity,
      tierId: tier._id,
    });
  }

  const isDisabled = submitting || !form.name || !form.email || !form.phone;

  return (
    <div className="booking-modal__backdrop" onClick={onClose}>
      <div className="booking-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Drag handle (mobile only) ──────────────── */}
        <div className="bm-drag-handle" />

        {/* ── Close ─────────────────────────────────── */}
        <button
          className="booking-modal__close"
          onClick={onClose}
          aria-label="Tutup"
        >
          <X size={16} />
        </button>

        <div className="bm-layout">

          {/* ══════════════════════════════════════════
              LEFT — event summary + qty + total
          ══════════════════════════════════════════ */}
          <div className="bm-left">

            {/* Timer */}
            <div className={`bm-timer${isUrgent ? " urgent" : ""}`}>
              <Timer size={13} />
              Tiket ditahan selama&nbsp;
              <strong>{minutes}:{seconds}</strong>
            </div>

            {/* Event info */}
            <p className="bm-event-label">Event</p>
            <h2 className="bm-event-title">{event.title}</h2>
            <p className="bm-event-date">
              <Calendar size={13} />
              {moment(event.date).locale("id").format("dddd, D MMMM YYYY")}
              {(event.timeStart || event.timeEnd) && (
                <> &middot; {event.timeStart}{event.timeEnd ? `–${event.timeEnd}` : ""}</>
              )}
            </p>

            <div className="bm-divider" />

            {/* Tier */}
            <div className="bm-tier">
              <span className="bm-tier__name">{tier.name}</span>
              <span className="bm-tier__price">
                Rp {tier.price.toLocaleString("id-ID")} / tiket
              </span>
            </div>

            {/* Quantity */}
            <div className="bm-qty">
              <span className="bm-qty__label">Jumlah tiket</span>
              <div className="bm-qty__controls">
                <button
                  className="bm-qty__btn"
                  disabled={quantity <= 1}
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                >
                  −
                </button>
                <span className="bm-qty__val">{quantity}</span>
                <button
                  className="bm-qty__btn"
                  disabled={quantity >= maxQty}
                  onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                >
                  +
                </button>
              </div>
            </div>
            <p className="bm-qty__max">Maks {maxQty} tiket per pemesanan</p>

            {/* Total */}
            <div className="bm-total">
              <span className="bm-total__label">Total</span>
              <span className="bm-total__amount">
                Rp {total.toLocaleString("id-ID")}
              </span>
            </div>
          </div>

          {/* ══════════════════════════════════════════
              RIGHT — form
          ══════════════════════════════════════════ */}
          <div className="bm-right">
            <p className="bm-form-title">Data Pemesan</p>

            {/* Payment method */}
            <div className="bm-payment-badge">
              <CreditCard size={13} />
              Pembayaran via QRIS
            </div>

            <div className="bm-form">
              <div className="bm-field">
                <label htmlFor="bm-name">Nama Lengkap</label>
                <input
                  id="bm-name"
                  placeholder="Nama sesuai identitas"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                />
              </div>

              <div className="bm-field">
                <label htmlFor="bm-email">Alamat Email</label>
                <input
                  id="bm-email"
                  type="email"
                  placeholder="email@contoh.com"
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                />
                <p className="bm-field__hint">Tiket akan dikirimkan ke email ini</p>
              </div>

              <div className="bm-field">
                <label htmlFor="bm-phone">Nomor WhatsApp</label>
                <input
                  id="bm-phone"
                  placeholder="08xxxxxxxxxx"
                  value={form.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                />
              </div>

              <button
                className="bm-submit"
                disabled={isDisabled}
                onClick={submit}
              >
                {submitting ? (
                  <span className="bm-spinner" />
                ) : null}
                {submitting ? "Memproses..." : "Pesan Sekarang"}
              </button>

              <p className="bm-hint">
                Kamu akan diarahkan ke halaman pembayaran QRIS setelah ini.
              </p>
            </div>
          </div>

        </div>

        {/* ── Mobile sticky footer ───────────────────── */}
        <div className="bm-mobile-footer">
          <div className="bm-mf-total">
            <span>Total</span>
            <strong>Rp {total.toLocaleString("id-ID")}</strong>
          </div>
          <button
            className="bm-submit"
            disabled={isDisabled}
            onClick={submit}
          >
            {submitting ? <span className="bm-spinner" /> : null}
            {submitting ? "Memproses..." : "Pesan Sekarang"}
          </button>
        </div>

      </div>
    </div>
  );
}
